/**
 * Client Filter Component
 * Componente de filtro centralizado para clientes (tenants) - estilo vover
 * Permite filtrar por nome ou ID do cliente
 */

import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  OutlinedInput,
  MenuProps,
  TextField,
  InputAdornment,
  Divider,
  ListSubheader,
  Checkbox,
  ListItemText,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Tooltip
} from '@mui/material';
import { Search, CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material';
import { apiService } from '@/services/api';
import { logger } from '@/utils/logger';

export interface Client {
  _id: string;
  tenantId: string;
  companyName: string;
  cnpj?: string;
}

interface ClientFilterProps {
  selectedClients: string[];
  onClientsChange: (clientIds: string[]) => void;
  multiple?: boolean;
}

export const ClientFilter: React.FC<ClientFilterProps> = ({
  selectedClients,
  onClientsChange,
  multiple = true
}) => {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Carregar todos os clientes inicialmente
  useEffect(() => {
    const loadClients = async () => {
      try {
        setLoading(true);
        const response = await apiService.getTenants({
          limit: 1000, // Carregar muitos clientes
          isActive: true
        });

        if (response.data.success) {
          const tenants = response.data.data || [];
          setClients(
            tenants.map((tenant: any) => ({
              _id: tenant._id || tenant.tenantId,
              tenantId: tenant.tenantId,
              companyName: tenant.companyName,
              cnpj: tenant.cnpj
            }))
          );
        }
      } catch (error) {
        logger.error('Error loading clients:', error);
        setClients([]);
      } finally {
        setLoading(false);
      }
    };

    loadClients();
  }, []);

  const handleChange = (event: any) => {
    const value = event.target.value;
    // Filtrar o valor especial "__SELECT_ALL__" se estiver presente
    const filteredValue =
      typeof value === 'string'
        ? value.split(',').filter((v: string) => v !== '__SELECT_ALL__')
        : value.filter((v: string) => v !== '__SELECT_ALL__');
    onClientsChange(filteredValue);
  };

  const handleSelectAll = () => {
    const allClientIds = clients.map((client: Client) => client.tenantId || client._id);
    onClientsChange(allClientIds);
  };

  const handleClearAll = () => {
    onClientsChange([]);
  };

  // Filtrar clientes baseado no termo de busca
  const filteredClients = useMemo(() => {
    if (!searchTerm) return clients;
    return clients.filter(
      (client: Client) =>
        client.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (client.tenantId && client.tenantId.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [clients, searchTerm]);

  const allSelected = clients.length > 0 && selectedClients.length === clients.length;
  const someSelected = selectedClients.length > 0 && selectedClients.length < clients.length;

  const menuProps: Partial<MenuProps> = {
    PaperProps: {
      style: {
        maxHeight: 400,
        width: 400,
        minWidth: 350
      }
    },
    autoFocus: false
  };

  return (
    <Box
      sx={{
        minWidth: { xs: '100%', md: 200 },
        mr: { xs: 0, md: 2 },
        width: { xs: '100%', md: 'auto' }
      }}
    >
      <FormControl fullWidth size="small">
        <Select
          multiple
          displayEmpty
          value={selectedClients}
          onChange={handleChange}
          input={<OutlinedInput />}
          renderValue={(selected: any) => {
            if (selected.length === 0) {
              return <em style={{ color: theme.palette.text.disabled }}>Filtrar por cliente</em>;
            }
            if (selected.length === 1) {
              const selectedId = String(selected[0]);
              const client = clients.find((c: Client) => {
                const clientId = c.tenantId || c._id;
                return String(clientId) === selectedId;
              });
              return <span>{client ? client.companyName : selectedId}</span>;
            }
            return <span>{selected.length} clientes selecionados</span>;
          }}
          MenuProps={menuProps}
          sx={{
            backgroundColor: isMobile ? 'background.paper' : 'white',
            borderRadius: '4px',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: isMobile ? 'divider' : 'rgba(255, 255, 255, 0.3)'
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: isMobile ? 'primary.main' : 'rgba(255, 255, 255, 0.5)'
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: isMobile ? 'primary.main' : 'white'
            }
          }}
        >
          {/* Campo de busca */}
          <ListSubheader>
            <TextField
              size="small"
              autoFocus
              placeholder="Buscar cliente..."
              fullWidth
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              onKeyDown={e => {
                if (e.key !== 'Escape') {
                  e.stopPropagation();
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                )
              }}
            />
          </ListSubheader>

          {/* Opções de selecionar/limpar todos */}
          <MenuItem
            value="__SELECT_ALL__"
            onMouseDown={e => {
              e.preventDefault();
              e.stopPropagation();
              if (allSelected) {
                handleClearAll();
              } else {
                handleSelectAll();
              }
            }}
            sx={{
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.08)'
              }
            }}
          >
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected}
              icon={<CheckBoxOutlineBlank />}
              checkedIcon={<CheckBox />}
              sx={{ p: 0, mr: 1 }}
            />
            <ListItemText
              primary={allSelected ? 'Desmarcar todos' : 'Selecionar todos'}
              primaryTypographyProps={{
                fontWeight: 'bold',
                color: 'primary.main'
              }}
            />
          </MenuItem>

          <Divider />

          {/* Lista de clientes filtrados */}
          {loading ? (
            <MenuItem disabled>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <CircularProgress size={20} />
                <em>Carregando clientes...</em>
              </Box>
            </MenuItem>
          ) : filteredClients.length === 0 ? (
            <MenuItem disabled>
              <em>Nenhum cliente encontrado</em>
            </MenuItem>
          ) : (
            filteredClients.map((client: Client) => {
              const clientId = client.tenantId || client._id;
              return (
                <MenuItem
                  key={clientId}
                  value={clientId}
                  sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    minHeight: '40px',
                    py: 1
                  }}
                >
                  <Checkbox
                    checked={selectedClients.includes(String(clientId))}
                    sx={{ p: 0, mr: 1 }}
                  />
                  <Tooltip title={client.companyName} arrow placement="top">
                    <Box
                      sx={{
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        pr: 1,
                        cursor: 'pointer'
                      }}
                    >
                      {client.companyName}
                    </Box>
                  </Tooltip>
                </MenuItem>
              );
            })
          )}
        </Select>
      </FormControl>
    </Box>
  );
};

export default ClientFilter;
